import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User, Share2, Link as LinkIcon, Tag } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { BlogCard } from "@/components/BlogCard";
import { useApp } from "@/context/AppContext";
import { api, imgSrc } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSeo } from "@/pages/Blog";

const ShareBar = ({ title }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const enc = encodeURIComponent;
  const links = [
    { label: "WhatsApp", href: `https://wa.me/?text=${enc(`${title} — ${url}`)}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { label: "X", href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}` },
  ];
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch (e) { toast.error("Could not copy"); } };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="blog-share">
      <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]"><Share2 className="h-3.5 w-3.5" /> Share</span>
      {links.map((l) => <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`share-${l.label.toLowerCase()}`} className="rounded-full border border-[var(--border-subtle)] px-3 py-1 text-xs text-[var(--text-primary)] hover:border-[var(--accent-gold)] hover:text-[var(--accent-gold)]">{l.label}</a>)}
      <button onClick={copy} data-testid="share-copy" className="inline-flex items-center gap-1 rounded-full border border-[var(--border-subtle)] px-3 py-1 text-xs text-[var(--text-primary)] hover:border-[var(--accent-gold)] hover:text-[var(--accent-gold)]"><LinkIcon className="h-3 w-3" /> Copy link</button>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const { data } = useApp();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    api.get(`/public/blogs/${slug}`).then((r) => { if (alive) { setPost(r.data); setStatus("ok"); } }).catch(() => alive && setStatus("missing"));
    return () => { alive = false; };
  }, [slug]);

  useSeo(post ? `${post.metaTitle || post.title} — Archedia Solutions` : null, post?.metaDescription || post?.excerpt);

  if (status === "missing") {
    return (
      <div className="container-arch flex min-h-[70vh] flex-col items-center justify-center text-center" data-testid="blog-not-found">
        <h1 className="font-serif text-4xl text-[var(--text-primary)]">Article not found</h1>
        <Link to="/blog" className="btn-outline-gold mt-6">Back to Blogs</Link>
      </div>
    );
  }
  if (!post) return <div className="container-arch pt-40 text-center text-sm text-[var(--text-muted)]">Loading article…</div>;

  const related = (data.blogs || []).filter((b) => b.id !== post.id && b.category === post.category).slice(0, 3);
  const fallback = related.length ? related : (data.blogs || []).filter((b) => b.id !== post.id).slice(0, 3);

  return (
    <article className="pb-24 pt-24" data-testid="blog-post">
      <div className="container-arch max-w-4xl">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-[var(--text-muted)] hover:text-[var(--accent-gold)]" data-testid="blog-back"><ArrowLeft className="h-4 w-4" /> All blogs</Link>
        <Reveal>
          <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="mt-6 inline-block text-[10px] font-semibold uppercase tracking-widest text-[var(--accent-gold)]">{post.category}</Link>
          <h1 className="mt-3 font-serif text-4xl font-light leading-tight text-[var(--text-primary)] sm:text-5xl" data-testid="blog-title">{post.title}</h1>
          {post.excerpt && <p className="mt-4 text-lg text-[var(--text-muted)]">{post.excerpt}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs text-[var(--text-muted)]">
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {post.author}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {formatDate(post.publishedAt || post.createdAt)}</span>
          </div>
        </Reveal>
      </div>

      {post.coverImage && (
        <div className="container-arch mt-10 max-w-5xl">
          <div className="overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-card)]">
            <img src={imgSrc(post.coverImage)} alt={post.title} className="max-h-[70vh] w-full object-contain" data-testid="blog-cover" />
          </div>
        </div>
      )}

      <div className="container-arch mt-10 max-w-3xl">
        <div className="prose-arch" dangerouslySetInnerHTML={{ __html: post.content || "" }} data-testid="blog-content" />
        {post.tags?.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="blog-tags">
            <Tag className="h-3.5 w-3.5 text-[var(--text-muted)]" />
            {post.tags.map((t) => <span key={t} className="rounded-full bg-[var(--accent-a10)] px-3 py-1 text-xs text-[var(--text-primary)]">{t}</span>)}
          </div>
        )}
        <div className="mt-8 border-t border-[var(--border-subtle)] pt-6"><ShareBar title={post.title} /></div>

        <div className="mt-12 rounded-2xl border border-[var(--accent-a15)] bg-[var(--accent-a5)] p-8 text-center">
          <div className="overline">Ready when you are</div>
          <h3 className="mt-2 font-serif text-2xl text-[var(--text-primary)]">Let us find the property that fits your life.</h3>
          <Link to="/request" className="btn-gold mt-5" data-testid="blog-cta-find">Find My Property</Link>
        </div>
      </div>

      {fallback.length > 0 && (
        <div className="container-arch mt-20" data-testid="blog-related">
          <div className="overline">Related reading</div>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {fallback.map((b) => <BlogCard key={b.id} post={b} compact />)}
          </div>
        </div>
      )}
    </article>
  );
}
